/* eslint react/no-multi-comp: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

// Multibrand-UI Components
import Input from '@zg-rentals/multibrand-ui/lib/Input';
import ArrowDropdown from '@zg-rentals/multibrand-ui/lib/icons/ArrowDropdown';

import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';

// styles
import './style.scss';

const REF_PREFIX = 'ref-';

const propTypes = {
    // props
    className: PropTypes.string,
    inModal: PropTypes.bool,
    itemList: PropTypes.arrayOf(PropTypes.object),
    label: PropTypes.string,
    value: PropTypes.string,
    minKeystrokes: PropTypes.number,
    placeholder: PropTypes.string,
    onChangeCallbackDelay: PropTypes.number,
    isEditable: PropTypes.bool,
    // callbacks
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyUp: PropTypes.func,
    onTriggerAction: PropTypes.func,
    // error
    errorMessage: PropTypes.node,
};

const defaultProps = {
    className: '',
    inModal: false,
    itemList: [],
    minKeystrokes: 6,
    placeholder: 'search',
    label: '',
    value: '',
    onChangeCallbackDelay: 300,
    // callbacks
    onChange: () => {},
    onClick: () => {},
    onBlur: () => {},
    onKeyUp: () => {},
    onTriggerAction: () => {},
    // error
    errorMessage: '',
    isEditable: true,
};

/**
 * Autocomplete
 *
 *
 */
class Autocomplete extends React.Component {
    constructor(props) {
        super(props);

        // init state
        this.state = {
            displayItemList: false,
            itemList: this.props.itemList,
            selectedIndex: -1,
        };

        // bind methods
        this.acTimeout = this.acTimeout.bind(this);
        this.decrementSelectedIndex = this.decrementSelectedIndex.bind(this);
        this.incrementSelectedIndex = this.incrementSelectedIndex.bind(this);
        this.isValidString = this.isValidString.bind(this);
        this.renderList = this.renderList.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        // item list
        if (nextProps.itemList.length === 0) {
            this.setState({ displayItemList: false });
        } else if (nextProps.itemList !== this.state.itemList) {
            this.setState({
                itemList: nextProps.itemList,
                displayItemList: true,
                selectedIndex: 0,
            });
        }
    }

    // empty method to reference timeout
    acTimeout() {}

    isValidString(value) {
        value = value.trim();
        const { minKeystrokes } = this.props;
        const multipleWords = value.indexOf(' ') > -1;

        return value.length >= minKeystrokes && multipleWords;
    }

    onChange(event) {
        const { onChange, onChangeCallbackDelay, onTriggerAction } = this.props;
        const { value } = event.target;

        // parent component will update state
        onChange(value);

        if (value === '') {
            this.setState({
                displayItemList: false,
                selectedIndex: -1,
            });
        }

        // if min keystrokes or no value
        if (this.isValidString(value) || value.length === 0) {
            // throttle response
            clearTimeout(this.acTimeout);
            this.acTimeout = setTimeout(() => {
                onTriggerAction();
            }, onChangeCallbackDelay);
        }
    }

    onClick(item) {
        const { onClick } = this.props;

        // update state
        this.setState({
            displayItemList: false,
            selectedIndex: -1,
        });

        onClick(item);
    }

    onKeyDown(event) {
        const { itemList, selectedIndex } = this.state;
        const { value } = this.props;
        const { keyCode } = event;

        // if no results
        if (itemList.length === 0 || value === '') {
            return;
        }

        // if enter is pressed
        if (keyCode === KeyCodeConstants.ENTER) {
            if (selectedIndex !== -1) {
                // trigger on click with selected index item
                this.onClick(itemList[selectedIndex]);
            } else {
                // trigger on click with first item
                this.onClick(itemList[0]);
            }
        }

        // if tab is pressed, close list
        if (keyCode === KeyCodeConstants.TAB) {
            this.setState({
                displayItemList: false,
            });
        }

        // if arrow is pressed
        if (keyCode === KeyCodeConstants.DOWN_ARROW) {
            // increment selected index
            this.incrementSelectedIndex(selectedIndex + 1);
        }

        if (keyCode === KeyCodeConstants.UP_ARROW) {
            // increment selected index
            this.decrementSelectedIndex(selectedIndex - 1);
        }
    }

    decrementSelectedIndex(value) {
        const { itemList } = this.state;

        // if value is less than min
        if (value < 0) {
            // reset
            value = itemList.length - 1;
        }

        // update
        this.setState({ selectedIndex: value });
    }

    incrementSelectedIndex(value) {
        const { itemList } = this.state;

        // if value is greater than max
        if (value > itemList.length - 1) {
            // reset
            value = 0;
        }

        // update
        this.setState({ selectedIndex: value });
    }

    renderList(list) {
        const { selectedIndex } = this.state;
        const classNamesAutoCompleteResultList = cx('Autocomplete-Result-List', {
            'Autocomplete-Result-List-Modal': this.props.inModal,
        });

        return (
            <div className="Autocomplete-Result">
                {this.props.inModal && (
                    <ArrowDropdown
                        className="Autocomplete-Result-List-Arrow-Up"
                        size={20}
                    />
                )}
                <div className={classNamesAutoCompleteResultList}>
                    {list.map((item, index) => {
                        const key = item.id || index;
                        const refName = `${REF_PREFIX}${index}`;

                        const classNames = cx({
                            'Autocomplete-Result-Item': true,
                            [`Autocomplete-Result-Item-${index}`]: true,
                            active: `${refName}` === `${REF_PREFIX}${selectedIndex}`,
                        });

                        return (
                            <ListItem
                                key={key}
                                targetRef={refName}
                                classNames={classNames}
                                item={item}
                                onClick={this.onClick}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        const {
            className,
            disabled,
            errorMessage,
            isEditable,
            label,
            onBlur,
            onKeyUp,
            placeholder,
            value,
        } = this.props;
        const { displayItemList, itemList } = this.state;

        return (
            <div className={cx('Autocomplete', className)}>
                <div className="Autocomplete-InputContainer">
                    <Input
                        type="text"
                        placeholder={placeholder}
                        aria-label={label}
                        label={label}
                        value={value}
                        onBlur={onBlur}
                        onChange={this.onChange}
                        onKeyDown={this.onKeyDown}
                        onKeyUp={onKeyUp}
                        error={errorMessage}
                        disabled={!isEditable || disabled}
                    />
                </div>

                {displayItemList && this.renderList(itemList)}
            </div>
        );
    }
}

/*
 * Create component in this context
 * to pass parameter to method
 */
const ListItem = ({
    onClick: propsOnClick,
    item,
    targetRef,
    classNames,
}) => {
    const onClick = () => {
        propsOnClick(item, targetRef);
    };

    return (
        <div className={classNames} onClick={onClick}>
            {item.name}
        </div>
    );
};

Autocomplete.propTypes = propTypes;
Autocomplete.defaultProps = defaultProps;

export default Autocomplete;
